import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Location } from '@angular/common';
import { Router, NavigationStart } from '@angular/router';
import { IPatientRequestDTO, IPatientResponseDTO } from 'src/app/core/domain/patient';
import { PatientService } from 'src/app/core/services/patient.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { UtilsService } from 'src/app/shared/utils/utils.service';
import { Observable } from 'rxjs';
import { MasterTablesService, MasterDataOption, Country } from 'src/app/core/services/master-tables.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatStepperModule } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { ViewChild } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { TrimOnBlurDirective } from 'src/app/shared/directives/trim-on-blur.directive';

@Component({
  selector: 'app-modal-add-patient',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDatepickerModule,
    MatInputModule,
    MatNativeDateModule,
    MatDialogModule,
    MatIconModule,
    MatStepperModule,
    MatButtonModule,
    TrimOnBlurDirective
  ],
  templateUrl: './modal-add-patient.component.html',
  styleUrl: './modal-add-patient.component.scss'
})
export class ModalAddPatientComponent implements OnInit {
  @ViewChild('stepper') stepper!: MatStepper;

  form: FormGroup;
  isCheckedConsent = false;
  activeStep = 'form';
//  htmlLoading = false;
  consentHtml: SafeHtml | null = null;

  // Datepicker properties
  maxDate: Date;
  minDate: Date;
  startDate: Date;

  public masterTablesService = inject(MasterTablesService);
  private http = inject(HttpClient);
  private sanitizer = inject(DomSanitizer);
  private fb = inject(FormBuilder);
  private patientService = inject(PatientService);
  private cdr = inject(ChangeDetectorRef);
  private translate = inject(TranslateService);
  private utilService = inject(UtilsService);

  // Master data
  genders$: Observable<MasterDataOption[]>;
  relationships$: Observable<MasterDataOption[]>;
  countries$: Observable<Country[]>;

  constructor(
    public dialogRef: MatDialogRef<ModalAddPatientComponent>,
    private router: Router,
    private location: Location
  ) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        // Close the dialog when navigating away
        this.dialogRef.close();
        // Reset the URL to prevent the navigation from proceeding
        this.location.replaceState(window.location.pathname + window.location.search);
      }
    });
  }

  ngOnInit() {
    // Set maxDate and startDate to today's date
    const today = new Date();
    this.maxDate = today;
    this.startDate = new Date();
    this.startDate = today;
  
    // Set minDate to January 1, 1960
    this.minDate = new Date(1960, 0, 1);

    // Initialize the form with validators
    this.form = this.fb.group({
      patientInfo: this.fb.group({
        given_name: ['', Validators.required],
        family_name: ['', Validators.required],
        date_of_birth: ['', Validators.required],
        country_of_birth: ['', Validators.required],
        gender: ['', Validators.required],
        deceased: [false],
        deceased_date: [null],
        relationship: ['', Validators.required]
      }),
      consent: this.fb.group({
        consent_id: ['', Validators.required],
        language: [this.translate.currentLang]
      })
    });

    // Load master data for gender, relationship and country.
    this.genders$ = this.masterTablesService.getGenders();
    this.relationships$ = this.masterTablesService.getRelationships();
    this.countries$ = this.masterTablesService.getCountries();

    // Listen to the popstate event to handle back navigation
    history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', this.handlePopState);
  }

  ngOnDestroy() {
    // Clean up the event listener to prevent memory leaks
    window.removeEventListener('popstate', this.handlePopState);
  }

  // Handle the popstate event to close the dialog
  handlePopState = () => {
    this.dialogRef.close();
    this.location.replaceState(window.location.pathname + window.location.search);
  }

  // Patient form has been submitted, move to the next step and sign the consent form.
  nextStep() {
    this.patientService.getFileConsent().subscribe({
      next: (response) => {
        if (response) {
  
          this.form.patchValue({
            consent: {
              consent_id: response.id,
              language: this.translate.currentLang
            }
          });
  
          this.http.get(response.content_url, { responseType: 'text' }).subscribe({
            next: (htmlContent) => {
              this.consentHtml = this.sanitizer.bypassSecurityTrustHtml(htmlContent);
              // Move to next step AFTER content is ready
              setTimeout(() => this.stepper.next()); // 💡 forces change detection
            },
            error: (error) => {
              console.error('Error loading consent HTML content:', error);
            }
          });
        }
      },
      error: (error) => {
        console.log(error);
      }
    });
  }
  

  previousStep() {
    this.activeStep = 'form';
  }

  onSubmit(): void {
    if (!this.form.valid) return;

    const patientInfo = this.form.get('patientInfo')?.value;
    const consent = this.form.get('consent')?.value;

    const givenName = (patientInfo.given_name || '').trim();
    const familyName = (patientInfo.family_name || '').trim();

    const patient: IPatientRequestDTO = {
      ...patientInfo,
      name: `${givenName} ${familyName}`,
      given_name: givenName,
      family_name: familyName,
      date_of_birth: this.utilService.formatDateToString(patientInfo.date_of_birth),
      deceased_date: null,
      consent: {
        consent_id: consent.consent_id,
        language: consent.language
      }
    };

    this.patientService.createPatient(patient).subscribe((response: IPatientResponseDTO) => {
      this.dialogRef.close(response);
      window.location.reload();
    });
  }
  
}
